import { lazy, type ReactNode, Suspense } from "react";

import {
  createBrowserRouter,
  LoaderFunction,
  RouteObject,
  redirect,
} from "react-router-dom";

import FullScreenLoader from "@/components/FullScreenLoader.tsx";
import AdminLayoutV2 from "@/components/layouts/AdminLayoutV2.tsx";
import {
  siteSessionIdTokenKeyName,
  siteSessionRefresh,
  siteSessionTokenKeyName,
} from "@/config/environment";
import { isReadOnlyIdToken } from "@/modules/auth/permissions.ts";
import SignIn from "@/pages/auth/SignIn.tsx";
import { getCookie } from "@/tools/cookies.ts";

import { routes } from "./routes.ts";

const loadAuthCallback = () => import("@/pages/auth/AuthCallback");
const loadResetPassword = () => import("@/pages/auth/SetPassword");
const loadSendResetPassword = () => import("@/pages/auth/SendResetPassword");
const loadSourcesv2 = () => import("@/pages/sources/indexv2");
const loadMovementsV2 = () => import("@/pages/movements/indexv2");
const loadApprovalsV2 = () => import("@/pages/approvals/Approvals_v2");
const loadDataV2 = () => import("@/pages/data/Data_v2");
const loadMovementsVersusV2 = () =>
  import("@/pages/movements/MovementsVersusV2");
const loadViewsV2 = () => import("@/pages/views/Views_v2");
const loadErrorPage = () => import("@/pages/errors/ErrorPage.tsx");
const loadPage404 = () => import("@/pages/errors/Page404.tsx");

const AuthCallback = lazy(loadAuthCallback);
const ResetPassword = lazy(loadResetPassword);
const SendResetPassword = lazy(loadSendResetPassword);
const Sourcesv2 = lazy(loadSourcesv2);
const MovementsV2 = lazy(loadMovementsV2);
const ApprovalsV2 = lazy(loadApprovalsV2);
const DataV2 = lazy(loadDataV2);
const MovementsVersusV2 = lazy(loadMovementsVersusV2);
const ViewsV2 = lazy(loadViewsV2);
const ErrorPage = lazy(loadErrorPage);
const Page404 = lazy(loadPage404);

const requireAuth: LoaderFunction = async ({ request }) => {
  if (!hasActiveSession()) {
    const url = new URL(request.url);
    const returnTo = `${url.pathname}${url.search}`;
    return redirect(
      `${routes.signIn.absolute}?returnTo=${encodeURIComponent(returnTo)}`,
    );
  }
  return null;
};

const requireNotAuth: LoaderFunction = async () => {
  if (hasActiveSession()) {
    return redirect(routes.admin.absolute);
  }
  return null;
};

const requireWriteAccess: LoaderFunction = async ({ request }) => {
  if (!hasActiveSession()) {
    const url = new URL(request.url);
    const returnTo = `${url.pathname}${url.search}`;
    return redirect(
      `${routes.signIn.absolute}?returnTo=${encodeURIComponent(returnTo)}`,
    );
  }

  if (isReadOnlyIdToken(getCookie(siteSessionIdTokenKeyName))) {
    return redirect(routes.movement_v2.absolute);
  }

  return null;
};

function hasActiveSession(): boolean {
  const token = getCookie(siteSessionTokenKeyName);
  const refresh = getCookie(siteSessionRefresh);
  return token !== null && refresh !== null;
}

const withSuspense = (element: ReactNode) => (
  <Suspense fallback={<FullScreenLoader />}>{element}</Suspense>
);

const normalizeRouteForPreload = (path: string) => {
  if (path === routes.admin.absolute || path === routes.root.path) {
    return routes.movement_v2.absolute;
  }

  return path;
};

export const preloadRouteModules = async (path: string) => {
  const normalizedPath = normalizeRouteForPreload(path);
  const tasks: Array<Promise<unknown>> = [];

  if (
    normalizedPath === routes.movement_v2.absolute ||
    normalizedPath.startsWith(`${routes.movement_v2.absolute}?`)
  ) {
    tasks.push(loadMovementsV2());
  } else if (
    normalizedPath === routes.approvals_v2.absolute ||
    normalizedPath.startsWith(`${routes.approvals_v2.absolute}?`)
  ) {
    tasks.push(loadApprovalsV2());
  } else if (
    normalizedPath === routes.data_v2.absolute ||
    normalizedPath.startsWith(`${routes.data_v2.absolute}?`)
  ) {
    tasks.push(loadDataV2());
  } else if (
    normalizedPath === routes.sources_v2.absolute ||
    normalizedPath.startsWith(`${routes.sources_v2.absolute}?`)
  ) {
    tasks.push(loadSourcesv2());
  } else if (
    normalizedPath === routes.views_v2.absolute ||
    normalizedPath.startsWith(`${routes.views_v2.absolute}?`)
  ) {
    tasks.push(loadViewsV2());
  } else if (
    normalizedPath.startsWith(`${routes.movement_v2.absolute}/`) &&
    normalizedPath.endsWith("/edit")
  ) {
    tasks.push(loadMovementsVersusV2());
  }

  if (tasks.length === 0) {
    return;
  }

  await Promise.all(tasks);
};

export const browserRoutes: RouteObject[] = [
  {
    path: routes.root.path,
    loader: () => {
      return hasActiveSession()
        ? redirect(routes.admin.absolute)
        : redirect(routes.signIn.absolute);
    },
  },
  {
    path: routes.signIn.path,
    loader: requireNotAuth,
    element: <SignIn />,
  },
  {
    path: routes.authCallback.path,
    element: withSuspense(<AuthCallback />),
  },
  {
    path: routes.admin.path,
    loader: requireAuth,
    element: <AdminLayoutV2 />,
    errorElement: withSuspense(<ErrorPage />),
    children: [
      {
        index: true,
        loader: () => redirect(routes.movement_v2.absolute),
      },
      {
        path: routes.sources_v2.path,
        element: withSuspense(<Sourcesv2 />),
      },
      {
        path: routes.movement_v2.path,
        element: withSuspense(<MovementsV2 />),
      },
      {
        path: routes.approvals_v2.path,
        element: withSuspense(<ApprovalsV2 />),
      },
      {
        path: routes.data_v2.path,
        loader: requireWriteAccess,
        element: withSuspense(<DataV2 />),
      },
      {
        path: routes.movementEdit_v2.path,
        loader: requireWriteAccess,
        element: withSuspense(<MovementsVersusV2 />),
      },
      {
        path: routes.views_v2.path,
        element: withSuspense(<ViewsV2 />),
      },
    ],
  },
  {
    path: routes.resetPassword.path,
    element: withSuspense(<ResetPassword />),
  },
  {
    path: routes.sendResetPassword.path,
    element: withSuspense(<SendResetPassword />),
  },
  {
    path: "*",
    element: withSuspense(<Page404 />),
  },
];

const router = createBrowserRouter(browserRoutes, {
  future: {
    v7_normalizeFormMethod: true,
  },
});

export default router;
